import {
  Square,
  Heading,
  Stack,
  Copy,
  Row,
  Column,
  Loader,
  Radio,
} from '@grubhubprod/gh-cookbook-react/dist';
import React, { useEffect, useState } from 'react';
import { Button, Flex, Typography } from '@grubhubprod/gh-cookbook-react';
import { useForm } from 'react-hook-form/dist/index.ie11';
import classnames from 'classnames';
import { Breadcrumbs } from 'src/components/Breadcrumbs/Breadcrumbs';
import PageHeader from 'src/components/enterprise/layout/PageHeader';
import { sdk } from 'src/utilities/sdk';
import {
  pushError,
  pushErrorFromAPI,
  pushSuccess,
} from 'src/utilities/notifications';
import { useAuth } from 'src/components/AuthProvider/AuthProvider';
import { ICreditCard } from 'src/types/CreditCard';
import { EnterpriseAutoPaymentModal } from 'src/components/enterprise/EnterprisePayments/EnterpriseAutoPaymentModal';
import { getAutoPaymentTerms } from 'src/components/enterprise/EnterprisePayments/EnterprisePayments.utils';
import { EnterpriseInvoicePaymentCreditCardForm } from 'src/components/enterprise/EnterprisePayments/EnterpriseInvoicePaymentCreditCardForm';
import PageSection from 'src/components/enterprise/layout/PageSection';
import { getDateFormatFromUTC } from 'src/utilities/formatDate';
import { buildCMUIPath, CMUIPage, getHomeBreadcrumb } from 'src/constants/urls';
import { RouterButtonLink } from 'src/components/RouterLink/RouterButtonLink';
import { decamelizeRequestBody } from 'src/utilities/http-data-casing';

export enum CreditCardAutoPayOption {
  ON = 'ON',
  OFF = 'OFF',
}

export default function EnterprisePayments(): JSX.Element {
  const {
    corpData,
    enterpriseData: {
      isFinancialAdminUser,
      isInternalAdminUser,
      isParentAdminUser,
    },
    corpId,
    redirectToHomePage,
  } = useAuth();
  const [card, setCard] = useState<ICreditCard>(null);
  const [showAutoPaymentModal, setShowAutoPaymentModal] = useState(false);
  const [showDeletionConfirmationMessage, setShowDeletionConfirmationMessage] =
    useState(false);
  const [isCardBeingDeleted, setIsCardBeingDeleted] = useState(false);
  const [showCardForm, setShowCardForm] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { register, handleSubmit, formState, reset, watch, setValue } = useForm(
    {
      mode: 'onChange',
      defaultValues: {
        creditCardAutopay: corpData.creditCardAutopay,
      },
    }
  );
  const { isValid, isSubmitting, isDirty } = formState;
  const hasCreditCard = !!card;
  const canManageCreditCard =
    (isFinancialAdminUser || isParentAdminUser) && hasCreditCard;
  const creditCardAutopay = watch('creditCardAutopay');
  const hasCorpCreditCardPrimary =
    corpData.invoicePaymentMethod === 'CREDIT_CARD_PRIMARY';

  const canUserAccessPayments =
    isFinancialAdminUser || isInternalAdminUser || isParentAdminUser;

  useEffect(() => {
    if (corpId) {
      if (canUserAccessPayments && hasCorpCreditCardPrimary) {
        getCreditCard();
      } else {
        redirectToHomePage(corpId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    corpId,
    isFinancialAdminUser,
    isInternalAdminUser,
    isParentAdminUser,
    canUserAccessPayments,
    hasCorpCreditCardPrimary,
  ]);

  async function getCreditCard(): Promise<void> {
    setIsLoading(true);
    try {
      let paymentApiClientTokenUrl = `/payments/corporate/${corpId}/credit_card`;

      if (isInternalAdminUser) {
        paymentApiClientTokenUrl = `${paymentApiClientTokenUrl}/internal_admin`;
      }

      const [card] = await sdk.http.get(paymentApiClientTokenUrl);
      setCard(card);
    } catch (error) {
      pushErrorFromAPI(error);
    } finally {
      setIsLoading(false);
    }
  }

  function getExpDateFromCard(month: string, year: string): string {
    return `${month.length < 2 ? `0${month}` : month}/${year.substring(2, 4)}`;
  }

  async function removeCard(): Promise<void> {
    setIsCardBeingDeleted(true);
    try {
      await sdk.http.delete(
        `/payments/corporate/${corpId}/payments/${card.id}`
      );

      setCard(null);
      if (corpData.creditCardAutopay === CreditCardAutoPayOption.ON) {
        await updateCorp(CreditCardAutoPayOption.OFF);
      }
      reset({ creditCardAutopay: CreditCardAutoPayOption.OFF });
      pushSuccess('Your credit card has been removed');
    } catch (e) {
      pushErrorFromAPI(e, {
        fallbackMessage: 'Your credit card has not been removed. Try again.',
      });
    } finally {
      setIsCardBeingDeleted(false);
      setShowDeletionConfirmationMessage(false);
    }
  }

  async function onAddCardSuccess(): Promise<void> {
    setShowCardForm(false);
    await getCreditCard();
    setShowAutoPaymentModal(true);
  }

  async function onAddCardError(): Promise<void> {
    pushError('Your credit card was not added. Try again.');
  }

  async function onFormSubmit({
    creditCardAutopay: creditCardAutopayValue,
  }): Promise<void> {
    try {
      await updateCorp(creditCardAutopayValue);
      pushSuccess('Your payment info has been updated');
      reset({ creditCardAutopay: creditCardAutopayValue });
    } catch (e) {
      pushErrorFromAPI(e, {
        fallbackMessage: 'Your payment info was not updated. Try again.',
      });
    }
  }

  async function updateCorp(
    creditCardAutopayValue: CreditCardAutoPayOption
  ): Promise<void> {
    await sdk.http.put(
      `/corps/${corpId}`,
      decamelizeRequestBody({
        ...corpData,
        creditCardAutopay: creditCardAutopayValue,
      })
    );
  }

  function setCreditCardAutoPay(
    creditCardAutoPay: CreditCardAutoPayOption
  ): void {
    setValue('creditCardAutopay', creditCardAutoPay);
    setShowAutoPaymentModal(false);
  }

  // no need to show page if corp invoice_payment_method is not CREDIT_CARD_PRIMARY
  // or the user is not an internal admin, financial admin, or parent admin
  if (!canUserAccessPayments || !hasCorpCreditCardPrimary) {
    return null;
  }

  return (
    <Square inset="lg">
      <PageHeader>
        <Stack y="xxx-sm">
          <Heading variant="h1">Invoice Payment Method</Heading>
        </Stack>
        <Breadcrumbs
          breadcrumbs={[
            {
              label: 'Admin home',
              ...getHomeBreadcrumb(corpId),
            },
          ]}
        />
      </PageHeader>
      <Stack y="md">
        <PageSection>
          <Stack y="xx-sm">
            <Heading variant="h4">Credit Card</Heading>
          </Stack>
          {isLoading ? (
            <Flex justifyContent="center">
              <Loader size="md" />
            </Flex>
          ) : (
            <Stack y="sm">
              <Stack y="md">
                {hasCreditCard ? (
                  <>
                    <Flex alignItems="center">
                      <Stack
                        x="x-sm"
                        className={classnames('creditCardIcon', {
                          [card.credit_card_type]: true,
                        })}
                      />
                      <Stack x="x-sm">
                        <Flex alignItems="center">
                          <Stack
                            style={{ textTransform: 'uppercase' }}
                            x="xxx-sm"
                          >
                            <Typography weight="bold">
                              {card.credit_card_type}
                            </Typography>
                          </Stack>
                          <Stack x="xxx-sm">
                            ending in {card.credit_card_last4}, exp
                          </Stack>
                          <Stack x="lg">
                            {getExpDateFromCard(
                              card.expiration_month.toString(),
                              card.expiration_year.toString()
                            )}
                          </Stack>
                          {canManageCreditCard && (
                            <>
                              {showDeletionConfirmationMessage ? (
                                <Flex alignItems="center">
                                  <Stack x="x-sm">
                                    <Copy variant="body">Delete Card?</Copy>
                                  </Stack>
                                  <Stack x="x-sm">
                                    <Button
                                      variant="primary"
                                      isLoading={isCardBeingDeleted}
                                      disabled={isCardBeingDeleted}
                                      onClick={removeCard}
                                    >
                                      Yes
                                    </Button>
                                  </Stack>
                                  <Button
                                    disabled={isCardBeingDeleted}
                                    variant="secondary"
                                    onClick={() =>
                                      setShowDeletionConfirmationMessage(false)
                                    }
                                  >
                                    No
                                  </Button>
                                </Flex>
                              ) : (
                                <Button
                                  variant="tertiary"
                                  onClick={() =>
                                    setShowDeletionConfirmationMessage(true)
                                  }
                                >
                                  Delete
                                </Button>
                              )}
                            </>
                          )}
                        </Flex>
                      </Stack>
                    </Flex>
                  </>
                ) : (
                  <>
                    {!showCardForm ? (
                      <Button
                        variant="primary"
                        buttonSize="medium"
                        disabled={isInternalAdminUser}
                        onClick={() => setShowCardForm(true)}
                      >
                        Add credit card
                      </Button>
                    ) : (
                      <EnterpriseInvoicePaymentCreditCardForm
                        corpId={corpId}
                        onClose={() => setShowCardForm(false)}
                        onSuccess={onAddCardSuccess}
                        onError={onAddCardError}
                      />
                    )}
                  </>
                )}
              </Stack>
              <Stack y="x-sm">
                <form>
                  <Flex alignItems="center">
                    <Stack y="x-sm" x="lg">
                      <Copy
                        variant="body"
                        color={
                          canManageCreditCard ? 'primaryText' : 'secondaryText'
                        }
                      >
                        Pay invoices automatically using the credit card on
                        file?
                      </Copy>
                    </Stack>
                    <Stack x="md">
                      <Radio
                        id="pay-automatically"
                        name="creditCardAutopay"
                        value={CreditCardAutoPayOption.ON}
                        ref={register}
                        disabled={!canManageCreditCard}
                        checked={
                          creditCardAutopay === CreditCardAutoPayOption.ON
                        }
                        onChange={() =>
                          setCreditCardAutoPay(CreditCardAutoPayOption.ON)
                        }
                      >
                        Pay automatically
                      </Radio>
                    </Stack>
                    <Radio
                      id="pay-manually"
                      name="creditCardAutopay"
                      value={CreditCardAutoPayOption.OFF}
                      ref={register}
                      disabled={!canManageCreditCard}
                      checked={
                        creditCardAutopay === CreditCardAutoPayOption.OFF
                      }
                      onChange={() =>
                        setCreditCardAutoPay(CreditCardAutoPayOption.OFF)
                      }
                    >
                      Pay manually
                    </Radio>
                  </Flex>
                </form>
              </Stack>
              {creditCardAutopay === CreditCardAutoPayOption.ON && (
                <>
                  <Stack y="xxx-sm">
                    Your first/next auto payment will be{' '}
                    {getDateFormatFromUTC(corpData.corpNextAutopay)}.
                  </Stack>
                  <Copy variant="caption" color="secondaryText">
                    {getAutoPaymentTerms()}
                  </Copy>
                </>
              )}
            </Stack>
          )}
        </PageSection>
      </Stack>
      <Stack y="md">
        <PageSection>
          <>
            <Stack y="sm">
              <Heading variant="h4">Bank Payment</Heading>
            </Stack>
            To make a payment by ACH, check, or wire transfer, please contact
            your account manager.
          </>
        </PageSection>
      </Stack>
      <Row gutter={8}>
        <Column gutter={8} tablet={4} desktop={3}>
          <Button
            variant="primary"
            block
            buttonSize="medium"
            isLoading={isSubmitting}
            disabled={!isValid || isSubmitting || !isDirty}
            onClick={handleSubmit(onFormSubmit)}
          >
            Save preferences
          </Button>
        </Column>
        <Column
          gutter={8}
          tablet={4}
          desktop={3}
          style={{ textAlign: 'center' }}
        >
          <RouterButtonLink
            href={buildCMUIPath(CMUIPage.HOME, { corpId })}
            buttonSize="medium"
            buttonVariant="secondary"
            useBlockButton
            isButtonDisabled={isSubmitting}
          >
            Cancel
          </RouterButtonLink>
        </Column>
      </Row>
      {showAutoPaymentModal && (
        <EnterpriseAutoPaymentModal
          onSuccess={() => setCreditCardAutoPay(CreditCardAutoPayOption.ON)}
          corp={corpData}
          onClose={() => setCreditCardAutoPay(CreditCardAutoPayOption.OFF)}
        />
      )}
    </Square>
  );
}
